import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  Portal,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { assert, HookOutOfContextError } from "@jugl-web/utils";

import {
  createContext,
  FC,
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { MediaPanel } from "./components/MediaPanel";
import { TopPanel } from "./components/TopPanel";
import { FilePreviewConfig } from "./types";
import { inferTypeFromMimeType } from "./utils";
import { FileTile } from "./components/FileTile";

export interface FilePreviewContextValue {
  isOpen: boolean;
  currentIndex: number | null;
  previewFile: (config: FilePreviewConfig) => void;
  previewFiles: (configs: FilePreviewConfig[], initialFileUrl?: string) => void;
  closePreview: () => void;
  onPreviewClose?: () => void;
}

const FilePreviewContext = createContext<FilePreviewContextValue | null>(null);

type MediaState = "ready" | "loading" | "error";

interface FilePreviewProviderProps {
  children: JSX.Element;
}

export const FilePreviewProvider: FC<FilePreviewProviderProps> = ({
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [mediaState, setMediaState] = useState<MediaState>("loading");
  const [currentConfig, setCurrentConfig] = useState<FilePreviewConfig | null>(
    null
  );
  const [currentIndex, setCurrentIndex] = useState<number | null>(null);
  const [isZoomed, setIsZoomed] = useState(false);

  useEffect(() => {
    setIsZoomed(false);
  }, [currentConfig]);

  const [currentConfigs, setCurrentConfigs] = useState<
    FilePreviewConfig[] | null
  >(null);

  const getPreviewType = useCallback(() => {
    assert(!!currentConfig, "No file to preview");
    return inferTypeFromMimeType(currentConfig.mimeType);
  }, [currentConfig]);

  const previewFile = useCallback((config: FilePreviewConfig) => {
    setIsOpen(true);
    setCurrentConfig(config);
    setCurrentConfigs(null);
    setCurrentIndex(0);
  }, []);

  const previewFiles = useCallback(
    (configs: FilePreviewConfig[], initialFileUrl?: string) => {
      setIsOpen(true);
      setCurrentConfigs(configs);

      const initialIndex = configs.findIndex(
        (config) => config.url === initialFileUrl
      );

      const indexToUse = initialIndex !== -1 ? initialIndex : 0;

      setCurrentConfig(configs[indexToUse]);
      setCurrentIndex(indexToUse);
    },
    []
  );

  useEffect(() => {
    if (!isOpen) {
      currentConfig?.onPreviewClose?.();
      setCurrentConfigs(null);
    }
  }, [isOpen, currentConfig]);

  const handleClosePreview = useCallback(() => setIsOpen(false), []);

  const contextValue = useMemo<FilePreviewContextValue>(
    () => ({
      isOpen,
      currentIndex,
      previewFile,
      previewFiles,
      closePreview: handleClosePreview,
    }),
    [isOpen, currentIndex, previewFile, previewFiles, handleClosePreview]
  );

  useEffect(() => {
    if (currentConfig) {
      setMediaState(getPreviewType() !== "other" ? "loading" : "ready");
    }
  }, [currentConfig, getPreviewType]);

  useEffect(() => {
    if (isOpen) {
      document?.body?.classList.add("overflow-hidden");
    } else {
      document?.body?.classList.remove("overflow-hidden");
    }
  }, [isOpen]);

  return (
    <FilePreviewContext.Provider value={contextValue}>
      {children}
      <Portal>
        <Transition show={isOpen} as={Fragment}>
          <Dialog
            unmount
            as="div"
            className="jugl__border-box-component"
            onClose={() => !currentConfig?.isCloseDisabled && setIsOpen(false)}
          >
            <TransitionChild
              as={Fragment}
              enter="transition-opacity duration-200"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <DialogBackdrop className="fixed inset-0 z-[100] bg-[rgba(14,14,14,0.66)] backdrop-blur-[1px]" />
            </TransitionChild>

            <DialogPanel className="fixed top-0 left-0 bottom-0 right-0 z-[1300] flex h-[100dvh] w-screen flex-col">
              {currentConfig && !isZoomed && !currentConfig.isHeaderHidden && (
                <TransitionChild
                  as={Fragment}
                  enter="transition duration-200"
                  enterFrom="opacity-0 -translate-y-full"
                  enterTo="opacity-100 translate-y-0"
                  leave="transition duration-200"
                  leaveFrom="opacity-100 translate-y-0"
                  leaveTo="opacity-0 -translate-y-full"
                >
                  <TopPanel
                    fileName={currentConfig.name}
                    onDownload={currentConfig.onDownload}
                    onClose={() => setIsOpen(false)}
                    fileAuthorName={currentConfig.username}
                    isCloseDisabled={currentConfig.isCloseDisabled}
                    onDelete={currentConfig.onDelete}
                  />
                </TransitionChild>
              )}
              {currentConfig && (
                <TransitionChild
                  as={Fragment}
                  enter="transition duration-200"
                  enterFrom="opacity-0 scale-90"
                  enterTo="opacity-100 scale-100"
                  leave="transition duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-90"
                >
                  <MediaPanel
                    key={currentConfig.url}
                    config={currentConfig}
                    previewType={getPreviewType()}
                    isLoading={mediaState === "loading"}
                    hasError={mediaState === "error"}
                    onLoad={() => setMediaState("ready")}
                    onError={() => setMediaState("error")}
                    onDownload={currentConfig.onDownload}
                    onClose={() =>
                      !currentConfig.isCloseDisabled && setIsOpen(false)
                    }
                    inMultipleFilesMode={Boolean(currentConfigs?.length)}
                    isZoomed={isZoomed}
                    onZoomChange={setIsZoomed}
                  />
                </TransitionChild>
              )}
              {currentConfigs?.length && !isZoomed && (
                <TransitionChild
                  as={Fragment}
                  enter="transition duration-200"
                  enterFrom="opacity-0 scale-90"
                  enterTo="opacity-100 scale-100"
                >
                  <div className="mx-auto my-[calc(100dvh_*_0.065)] flex w-max max-w-[90%] flex-col gap-2.5 rounded-2xl bg-[#121516] p-4">
                    <div className="jugl__custom-scrollbar flex items-center gap-2.5 overflow-x-auto [--webkit-overflow-scrolling:touch]">
                      {currentConfigs?.map((file, index) => (
                        <FileTile
                          key={file.url}
                          file={file.url}
                          preview={file.preview}
                          isSelected={currentConfig?.url === file.url}
                          mimeType={file.mimeType}
                          onClick={() => {
                            setCurrentConfig(file);
                            setCurrentIndex(index);
                          }}
                        />
                      )) || ""}
                    </div>
                  </div>
                </TransitionChild>
              )}
            </DialogPanel>
          </Dialog>
        </Transition>
      </Portal>
    </FilePreviewContext.Provider>
  );
};

export const useFilePreview = () => {
  const context = useContext(FilePreviewContext);

  if (!context) {
    throw new HookOutOfContextError("useFilePreview", "FilePreviewContext");
  }

  return context;
};
